import Phaser from 'phaser';
import { PixelCanvas } from './pixel/PixelCanvas';
import type { Tree } from '@/domain/world/evolve';

export interface TreeRendererHandle {
  container: Phaser.GameObjects.Container;
  destroy(): void;
}

const PIXEL_SCALE = 4;

export function buildTreeRenderer(scene: Phaser.Scene, tree: Tree): TreeRendererHandle {
  const container = scene.add.container(tree.x, tree.y).setScale(PIXEL_SCALE);
  container.setDepth(5);
  const g = scene.add.graphics();
  const px = new PixelCanvas(g);
  drawTree(px, tree.stage);
  container.add(g);
  return {
    container,
    destroy(): void {
      container.destroy();
    },
  };
}

function drawTree(px: PixelCanvas, stage: Tree['stage']): void {
  const k = 0x1a1a14;
  const trunk = 0x6a4423;
  const trunkD = 0x3a2010;
  const greenL = 0x84d870;
  const greenM = 0x5fbb5a;
  const greenD = 0x2f7a3a;
  switch (stage) {
    case 'sapling':
      // Mudinha pequena: 2px tronco + 1 folha
      px.vLine(0, 0, 4, trunk);
      px.px(0, -1, greenM);
      px.px(1, -2, greenM);
      px.px(-1, -2, greenM);
      px.px(0, -3, greenL);
      // sombra
      px.rect(-2, 4, 5, 1, k);
      break;
    case 'small':
      // Pequena: tronco 3 alto + copa 5×3
      px.vLine(0, 0, 5, trunk);
      px.px(0, -2, trunk);
      px.rect(-2, -3, 5, 2, greenM);
      px.px(-3, -2, greenM);
      px.px(3, -2, greenM);
      px.rect(-2, -5, 5, 2, greenL);
      px.px(0, -6, greenL);
      // outline
      px.px(-3, -2, k);
      px.px(3, -2, k);
      px.rect(-3, 5, 7, 1, k);
      break;
    case 'adult':
      // Adulta: tronco grosso + copa volumosa
      px.rect(-1, 0, 3, 5, trunk);
      px.vLine(-1, 1, 4, trunkD);
      // copa
      px.rect(-3, -2, 7, 2, greenM);
      px.rect(-4, -3, 9, 1, greenM);
      px.rect(-3, -5, 7, 2, greenL);
      px.rect(-2, -7, 5, 2, greenL);
      px.px(-2, -2, greenD);
      px.px(2, -2, greenD);
      px.rect(-1, -8, 3, 1, greenL);
      // outline
      px.rect(-4, 5, 9, 1, k);
      break;
    case 'ancient':
      // Anciã: tronco mais largo + copa enorme
      px.rect(-2, 1, 5, 6, trunk);
      px.vLine(-2, 1, 6, trunkD);
      px.vLine(2, 1, 6, trunkD);
      // copa massiva (3 camadas)
      px.rect(-5, -3, 11, 4, greenM);
      px.rect(-4, -6, 9, 3, greenL);
      px.rect(-2, -9, 5, 3, greenL);
      px.px(-3, -2, greenD);
      px.px(3, -2, greenD);
      px.px(-1, -10, greenL);
      // raízes
      px.px(-3, 6, trunkD);
      px.px(3, 6, trunkD);
      // sombra grande
      px.rect(-5, 7, 11, 1, k);
      break;
  }
}
